/*
 * Visibility helpers — given a NavTree and a PermissionResolver, return
 * the filtered tree the current user should see.
 *
 * Platform-Admin-sees-ALL rule:
 *   resolver.isSuperAdmin === true  →  every group/item passes
 *   This is the single source of truth for the "Platform Admin must see
 *   and access all UI options" requirement. The matching server-side
 *   guard lives in services/tallystream-api/src/app.ts via
 *   isPlatformOperator(); both must stay in sync.
 *
 * Group visibility model:
 *   - If the group declares its own require* gates, those gates run
 *     first.
 *   - Otherwise the group is visible iff at least one item is visible.
 *   - Platform Super-Admin bypasses both — see above.
 */

import type {
  NavGroup,
  NavItem,
  NavTree,
  PermissionResolver
} from "./navigationTypes.js";

export function isItemVisible(item: NavItem, resolver: PermissionResolver): boolean {
  if (resolver.isSuperAdmin) return true;
  if (item.requirePermissions && item.requirePermissions.length > 0) {
    if (!item.requirePermissions.every((k) => resolver.has(k))) return false;
  }
  if (item.requireAnyPermission && item.requireAnyPermission.length > 0) {
    if (!resolver.hasAny(...item.requireAnyPermission)) return false;
  }
  return true;
}

export function isGroupVisible(group: NavGroup, resolver: PermissionResolver): boolean {
  if (resolver.isSuperAdmin) return true;
  if (group.requirePermissions && group.requirePermissions.length > 0) {
    if (!group.requirePermissions.every((k) => resolver.has(k))) return false;
  }
  if (group.requireAnyPermission && group.requireAnyPermission.length > 0) {
    if (!resolver.hasAny(...group.requireAnyPermission)) return false;
  }
  // No explicit gate ⇒ visible iff at least one child is visible.
  if (
    !group.requirePermissions?.length &&
    !group.requireAnyPermission?.length
  ) {
    return group.items.some((it) => isItemVisible(it, resolver));
  }
  return true;
}

/** Filter a tree down to the groups + items the current user can see. */
export function filterNavTree(tree: NavTree, resolver: PermissionResolver): NavTree {
  const groups = tree.groups
    .filter((g) => isGroupVisible(g, resolver))
    .map((g) => ({
      ...g,
      items: g.items.filter((it) => isItemVisible(it, resolver))
    }))
    .filter((g) => g.items.length > 0);
  return { ...tree, groups };
}

/** Flat list of every item the user can see — feeds the search index. */
export function visibleItems(tree: NavTree, resolver: PermissionResolver): NavItem[] {
  return filterNavTree(tree, resolver).groups.flatMap((g) => g.items);
}

/** Permission resolver shorthand for tests / non-React callers. */
export function makeResolver(opts: {
  permissions?: string[];
  isSuperAdmin?: boolean;
}): PermissionResolver {
  const set = new Set(opts.permissions ?? []);
  const isSuperAdmin = !!opts.isSuperAdmin;
  return {
    isSuperAdmin,
    has: (key) => isSuperAdmin || set.has(key),
    hasAny: (...keys) => isSuperAdmin || keys.some((k) => set.has(k))
  };
}
